// src/components/Generator/BulkResultsCard.js
import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { saveAs } from 'file-saver';
import SingleResultsCard from './SingleResultsCard';
import AuthContext from '../../context/AuthContext';
import SkuSelectionModal from '../UI/SkuSelectionModal';
import sidebarStyles from './BulkResultsCard.module.css';
import { AMAZON_MAINTENANCE_MODE } from '../../config/amazonMaintenance';
import { useNotification } from '../../context/NotificationContext';

const API_URL = process.env.REACT_APP_API_URL;

// ── Icons ────────────────────────────────────────────────────────────────────

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M7 0C3.15098 0 0 3.15083 0 7C0 10.8492 3.15083 14 7 14C10.8492 14 14 10.8492 14 7C14 3.15083 10.8492 0 7 0ZM9.55375 4.49815C9.94345 4.05763 10.613 4.64947 10.2233 5.09058L6.32807 9.50109C6.15922 9.69155 5.86417 9.70324 5.68189 9.52504L3.80002 7.68577C3.37761 7.27387 4.00276 6.63352 4.42401 7.04425L5.96996 8.55625L9.55375 4.49815Z" fill="#96C7FB"/>
    </svg>
);

const BackIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16.6667 9.16671H6.52504L11.1834 4.50837L10 3.33337L3.33337 10L10 16.6667L11.175 15.4917L6.52504 10.8334H16.6667V9.16671Z" fill="#505050"/>
    </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

const getPreviewImageFromSections = (sections) => {
    if (!sections) return null;
    return sections.primary?.main ||
        (sections.feature && Object.values(sections.feature)[0]) ||
        (sections.application && Object.values(sections.application)[0]) ||
        null;
};

const isParentItem = (item) => {
    const isParent = item?.isParent;
    return isParent === true || isParent === 'true' || isParent === 'TRUE';
};

// ── Sidebar SKU Card ──────────────────────────────────────────────────────────

const SkuCard = ({ item, isSelected, onClick, isChild, hideThumbnails, skuLabel }) => (
    <div
        className={`${sidebarStyles.skuCard} ${isSelected ? sidebarStyles.skuCardSelected : sidebarStyles.skuCardInactive} ${isChild ? sidebarStyles.skuCardChild : ''}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
        {!hideThumbnails && (
        <div className={sidebarStyles.skuThumb}>
            {item.thumbnail ? (
                <img src={item.thumbnail} alt={item.sku} loading="lazy" />
            ) : (
                <div className={sidebarStyles.skuThumbPlaceholder} />
            )}
            {isSelected && (
                <div className={sidebarStyles.skuCheck}><CheckIcon /></div>
            )}
        </div>
        )}
        <div className={sidebarStyles.skuInfo}>
            <div className={sidebarStyles.skuTitle}>{item.title}</div>
            <div className={sidebarStyles.skuBadge}>{skuLabel || 'SKU ID'} : {item.sku}</div>
                        </div>
        </div>
    );

// ── Main Component ────────────────────────────────────────────────────────────

const BulkResultsCard = ({
    onClose,
    selectedGeneration,  // SKU string of initially selected item
    bulkProjectData,
    preTransformedData,
    isDataLoading = false,
}) => {
    const authCtx = useContext(AuthContext);
    const { showAmazonMaintenanceMessage } = useNotification();

    // Build skuList from preTransformedData + bulkProjectData (fallback to image_urls when Excel data missing)
    const skuList = useMemo(() => {
        const imageUrls = bulkProjectData?.image_urls?.parent_skus || [];
        if (!imageUrls.length) return [];

        // Build O(1) lookup map for image data by SKU
        const imageUrlMap = new Map();
        imageUrls.forEach((d) => imageUrlMap.set(d.sku, d));

        const getThumbnail = (sku) => {
            const imgData = imageUrlMap.get(sku);
            return getPreviewImageFromSections(imgData?.sections);
        };

        const productData = preTransformedData?.length
            ? preTransformedData
            : imageUrls.map((d) => ({
                  sku: d.sku,
                  title: isDataLoading ? 'Loading...' : `Product ${d.sku}`,
                  isParent: d.is_parent === true || d.is_parent === 'true' || d.is_parent === 'TRUE',
                  parentSku: '',
              }));

        const parentSkus = productData.filter(isParentItem);
        const childSkus = productData.filter((item) => !isParentItem(item) && item.parentSku?.trim());

        // Pre-compute parent mapping for children with missing parentSku (O(n) instead of O(n²))
        const inferredParentMap = new Map();
        let lastParentSku = null;
        imageUrls.forEach((d) => {
            if (d.is_parent === true || d.is_parent === 'true' || d.is_parent === 'TRUE') {
                lastParentSku = d.sku;
            } else if (lastParentSku) {
                inferredParentMap.set(d.sku, lastParentSku);
            }
        });

        if (parentSkus.length === 0) {
            return productData.map((item) => ({
                sku: item.sku,
                title: item.title || `Product ${item.sku}`,
                thumbnail: getThumbnail(item.sku),
                isParent: false,
                children: [],
            }));
        }

        // Group children by parent SKU using Maps (O(n) instead of O(n²))
        const childrenByParent = new Map();
        const inferredChildrenByParent = new Map();
        childSkus.forEach((c) => {
            if (!childrenByParent.has(c.parentSku)) childrenByParent.set(c.parentSku, []);
            childrenByParent.get(c.parentSku).push(c);
        });
        productData.forEach((item) => {
            if (!isParentItem(item)) {
                const inferredParent = inferredParentMap.get(item.sku);
                if (inferredParent) {
                    if (!inferredChildrenByParent.has(inferredParent)) inferredChildrenByParent.set(inferredParent, []);
                    inferredChildrenByParent.get(inferredParent).push(item);
                }
            }
        });

        return parentSkus.map((parent) => {
            const children = childrenByParent.get(parent.sku) || [];
            const allChildren = children.length ? children : (inferredChildrenByParent.get(parent.sku) || []);
            return {
                sku: parent.sku,
                title: parent.title || `Product ${parent.sku}`,
                thumbnail: getThumbnail(parent.sku),
                isParent: true,
                children: allChildren.map((child) => ({
                    sku: child.sku,
                    title: child.title || `Product ${child.sku}`,
                    thumbnail: getThumbnail(child.sku),
                })),
            };
        });
    }, [preTransformedData, bulkProjectData, isDataLoading]);

    const [selectedSku, setSelectedSku] = useState(selectedGeneration || skuList[0]?.sku || skuList[0]?.children?.[0]?.sku || '');
    const [expandedParent, setExpandedParent] = useState(null);
    const [skuSearchTerm, setSkuSearchTerm] = useState('');
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showPushModal, setShowPushModal] = useState(false);
    const [modalSelectedSkus, setModalSelectedSkus] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPushingToAmazon, setIsPushingToAmazon] = useState(false);
    // Track per-SKU content edits so downloads reflect updated content
    const [skuContentUpdates, setSkuContentUpdates] = useState({});

    const handleContentUpdated = useCallback((updatedData) => {
        if (!selectedSku || updatedData?._isChildUpdate) return;
        setSkuContentUpdates(prev => ({
            ...prev,
            [selectedSku]: {
                title: updatedData.title,
                bullets: updatedData.bullets,
                description: updatedData.description,
            },
        }));
    }, [selectedSku]);

    const totalListings = skuList.length;
    const isNewListing = (bulkProjectData?.listing_mode || 'new').toLowerCase() !== 'existing';
    // Detect whether SKUs are ASINs (10-char alphanumeric starting with B0) based on first SKU
    const firstSku = skuList[0]?.sku || '';
    const isAsin = /^B0[A-Z0-9]{8}$/i.test(firstSku);
    const isProductCopy = bulkProjectData?.is_product_copy === true;

    const allSelectableSkus = useMemo(() => {
        const seen = new Set();
        const items = [];
        skuList.forEach((item) => {
            if (item.children?.length) {
                if (!seen.has(item.sku)) {
                    seen.add(item.sku);
                    items.push({ sku: item.sku, title: item.title, thumbnail: item.thumbnail });
                }
                item.children.forEach((c) => {
                    if (!seen.has(c.sku)) {
                        seen.add(c.sku);
                        items.push({ sku: c.sku, title: c.title, thumbnail: c.thumbnail });
                    }
                });
            } else {
                if (!seen.has(item.sku)) {
                    seen.add(item.sku);
                    items.push({ sku: item.sku, title: item.title, thumbnail: item.thumbnail });
                }
            }
        });
        return items;
    }, [skuList]);

    const filteredSkuList = useMemo(() => {
        if (!skuSearchTerm.trim()) return skuList;
        const term = skuSearchTerm.toLowerCase().trim();
        return skuList.filter((item) => {
            const matchItem = (t) =>
                (t.title && t.title.toLowerCase().includes(term)) ||
                (t.sku && t.sku.toLowerCase().includes(term));
            if (matchItem(item)) return true;
            if (item.children) {
                return item.children.some(matchItem);
            }
            return false;
        });
    }, [skuList, skuSearchTerm]);

    useEffect(() => {
        if (!skuList.length) return;
        if (selectedGeneration) {
            setSelectedSku(selectedGeneration);
            const parent = skuList.find((p) => p.children?.some((c) => c.sku === selectedGeneration));
            if (parent) setExpandedParent(parent.sku);
            } else {
            const firstSku = skuList[0]?.children?.length ? skuList[0].children[0].sku : skuList[0]?.sku;
            if (firstSku) setSelectedSku(firstSku);
        }
    }, [skuList.length, selectedGeneration]);

    const handleSkuClick = (sku) => setSelectedSku(sku);
    const handleParentClick = (parentSku) => {
        handleSkuClick(parentSku);
        setExpandedParent((prev) => (prev === parentSku ? null : parentSku));
    };

    const handleOpenDownloadModal = () => {
        setModalSelectedSkus(allSelectableSkus.map((i) => i.sku));
        setShowDownloadModal(true);
    };

    const handleOpenPushModal = () => {
        if (AMAZON_MAINTENANCE_MODE) {
            showAmazonMaintenanceMessage();
            return;
        }
        setModalSelectedSkus(allSelectableSkus.map((i) => i.sku));
        setShowPushModal(true);
    };

    const handleModalSkuToggle = useCallback((sku) => {
        setModalSelectedSkus((prev) =>
            prev.includes(sku) ? prev.filter((s) => s !== sku) : [...prev, sku]
        );
    }, []);

    const handleModalSelectAll = useCallback((skus) => {
        setModalSelectedSkus(skus);
    }, []);

    const handleDownloadConfirm = useCallback(async () => {
        if (modalSelectedSkus.length === 0) return;
        setIsDownloading(true);
        try {
            const generationId = bulkProjectData?._id;
            if (!generationId) {
                console.error('No generation ID available for download');
                return;
            }

            const response = await fetch(`${API_URL}/bulk-generation-v2/download-content`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    generation_id: generationId,
                    selected_skus: modalSelectedSkus,
                    content_updates: skuContentUpdates,
                }),
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                console.error('Download failed:', err.error || response.statusText);
                return;
            }

            const blob = await response.blob();
            saveAs(blob, `bulk_results_${bulkProjectData?.task_id || generationId}.zip`);
            setShowDownloadModal(false);
        } finally {
            setIsDownloading(false);
        }
    }, [modalSelectedSkus, bulkProjectData, skuContentUpdates, authCtx.token]);

    const handlePushConfirm = useCallback(async () => {
        if (modalSelectedSkus.length === 0) return;
        const token = authCtx.token;
        if (!token) return;
        setIsPushingToAmazon(true);
        let successCount = 0;
        let failCount = 0;
        for (const sku of modalSelectedSkus) {
            const rd = getResultDataForSku(sku);
            const co = rd.claude_output || {};
            const bullets = Array.isArray(co.bullets) ? co.bullets : [];
            const infographicUrls = co.infographic_urls || co.generated_images || [];
            const aplusUrls = (rd.aplus_templates || []).map((t) => t?.image_url?.url).filter(Boolean);

            const updateData = {
                asin: sku,
                updateListingData: {
                    title: co.title || '',
                    description: co.description || '',
                    bullets: bullets,
                    infographicUrls: infographicUrls.map((url) =>
                        url.startsWith('http://localhost:5000/static/') ? url.replace('http://localhost:5000', '') : url
                    ),
                    aplusImageUrls: aplusUrls.map((url) =>
                        url.startsWith('http://localhost:5000/static/') ? url.replace('http://localhost:5000', '') : url
                    ),
                },
            };

            try {
                const res = await fetch(`${API_URL}/oauth_amazon/spapi/update_amznProduct_listing`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(updateData),
                });
                if (res.ok) successCount++;
                else failCount++;
            } catch {
                failCount++;
            }
        }
        setShowPushModal(false);
        setIsPushingToAmazon(false);
    }, [modalSelectedSkus, authCtx.token]);

    // Pre-build O(1) lookup maps for getResultDataForSku
    const resultLookups = useMemo(() => {
        const productData = preTransformedData || [];
        const imageUrls = bulkProjectData?.image_urls?.parent_skus || [];
        const productMap = new Map();
        productData.forEach((r) => productMap.set(r.sku, r));
        const imageMap = new Map();
        imageUrls.forEach((d) => imageMap.set(d.sku, d));
        return { productMap, imageMap };
    }, [preTransformedData, bulkProjectData]);

    const getResultDataForSku = (sku) => {
        const excelRow = resultLookups.productMap.get(sku) || {};
        const skuImageData = resultLookups.imageMap.get(sku);
        const sections = skuImageData?.sections || {};

        const generatedImages = [];
        if (sections.primary?.main) generatedImages.push(sections.primary.main);
        if (sections.feature) Object.values(sections.feature).forEach((url) => generatedImages.push(url));
        if (sections.application) Object.values(sections.application).forEach((url) => generatedImages.push(url));
        if (sections.dimension) Object.values(sections.dimension).forEach((url) => generatedImages.push(url));

        const aplusTemplates = [];
        if (sections.aplus) {
            Object.values(sections.aplus).forEach((url, idx) => {
                aplusTemplates.push({ image_url: { url }, page: idx + 1 });
            });
        }

        const bullets = excelRow.features || excelRow.bullets || [];
        const bulletsArray = Array.isArray(bullets) ? bullets : [];

        const editorFolderId = excelRow.editor_folder_id || skuImageData?.editor_folder_id || bulkProjectData?.editor_folder_ids?.[sku] || null;

        // Apply any saved content edits for this SKU
        const edits = skuContentUpdates[sku];

        return {
            product: {
                model_number: sku,
                project_name: bulkProjectData?.project_name?.trim() || 'Untitled',
            },
            claude_output: {
                title: edits?.title ?? excelRow.title ?? `Product ${sku}`,
                description: edits?.description ?? excelRow.description ?? '',
                bullets: Array.isArray(edits?.bullets) ? edits.bullets : bulletsArray,
                key_feature_callouts: [],
                infographic_urls: generatedImages,
                generated_images: generatedImages,
            },
            child_products: [],
            child_infographics: [],
            aplus_templates: aplusTemplates,
            bplus_templates: generatedImages,
            inserted_id: bulkProjectData?._id || null,
            listing_mode: 'new',
            editor_folder_id: editorFolderId,
        };
    };

    const resultData = getResultDataForSku(selectedSku);

            return (
        <div className={sidebarStyles.pageWrapper}>
            <div className={sidebarStyles.topBar}>
                <button
                    type="button"
                    className={sidebarStyles.backButton}
                    onClick={onClose}
                    aria-label="Back to projects"
                >
                    <BackIcon />
                </button>
                <div className={sidebarStyles.topBarHeader}>
                    <div className={sidebarStyles.topBarHeaderLeft}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g opacity="0.7">
                        <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="#6794C5"/>
                        <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="#6794C5"/>
                        </g>
                        </svg>
                        <span className={sidebarStyles.topBarGeneratedText}>Generated {allSelectableSkus.length} Listings</span>
                    </div>
                    <span className={sidebarStyles.topBarProductCount}>
                        {(() => {
                            const idx = allSelectableSkus.findIndex((s) => s.sku === selectedSku);
                            const current = idx >= 0 ? idx + 1 : 1;
                            return `${current}/${allSelectableSkus.length} Products`;
                        })()}
                    </span>
                </div>
            </div>
            <div className={sidebarStyles.pageWrapperContent}>
            {/* Left column: SKU sidebar + buttons */}
            <div className={sidebarStyles.sidebarColumn}>
                <div className={sidebarStyles.sidebar}>
                    <div className={sidebarStyles.skuSearchWrapper}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.421 0C14.5954 0 18 3.40462 18 7.57899C18 11.7534 14.5954 15.158 10.421 15.158C8.67422 15.158 7.06155 14.5613 5.77761 13.5621L1.61719 17.7224C1.24712 18.0925 0.647591 18.0925 0.277544 17.7224C-0.0925028 17.3524 -0.0925265 16.7519 0.277544 16.3828L4.43796 12.2234C3.43876 10.9383 2.84203 9.32678 2.84203 7.58006C2.84203 3.40569 6.24668 0.00106589 10.4211 0.00106589L10.421 0ZM10.421 1.89475C7.27068 1.89475 4.73667 4.42874 4.73667 7.57899C4.73667 10.7292 7.27068 13.2632 10.421 13.2632C13.5712 13.2632 16.1052 10.7292 16.1052 7.57899C16.1052 4.42874 13.5712 1.89475 10.421 1.89475Z" fill="#D2D2D2"/>
                        </svg>
                        <input
                            type="text"
                            className={sidebarStyles.skuSearch}
                            placeholder="Search for Product Name or SKU ID"
                            value={skuSearchTerm}
                            onChange={(e) => setSkuSearchTerm(e.target.value)}
                            aria-label="Search SKUs"
                        />
                                </div>
                    <div className={sidebarStyles.skuList}>
                    {filteredSkuList.map((item) => {
                        const isParent = item.isParent && item.children?.length > 0;
                        const isExpanded = expandedParent === item.sku;

                        return (
                            <React.Fragment key={item.sku}>
                                <div
                                    className={`${sidebarStyles.skuCard} ${item.sku === selectedSku ? sidebarStyles.skuCardSelected : sidebarStyles.skuCardInactive}`}
                                    onClick={() => isParent ? handleParentClick(item.sku) : handleSkuClick(item.sku)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            isParent ? handleParentClick(item.sku) : handleSkuClick(item.sku);
                                        }
                                    }}
                                >
                                    {!isProductCopy && (
                                    <div className={sidebarStyles.skuThumb}>
                                        {item.thumbnail ? (
                                            <img src={item.thumbnail} alt={item.sku} loading="lazy" />
                                        ) : (
                                            <div className={sidebarStyles.skuThumbPlaceholder} />
                                        )}
                                        {item.sku === selectedSku && (
                                            <div className={sidebarStyles.skuCheck}><CheckIcon /></div>
                                        )}
                                    </div>
                                    )}
                                    <div className={sidebarStyles.skuInfo}>
                                        <div className={sidebarStyles.skuTitle}>{item.title}</div>
                                        <div className={sidebarStyles.skuBadge}>{isAsin ? 'ASIN' : 'SKU ID'} : {item.sku}</div>
                                        </div>
                                    {isParent && (
                                        <div className={sidebarStyles.chevron}>
                                            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                </div>
                            )}
                                    </div>

                                {isParent && isExpanded && (
                                    <div className={sidebarStyles.childSection}>
                                        <div className={sidebarStyles.childLabel}>Child variants</div>
                                        {item.children.map((child) => (
                                            <SkuCard
                                                key={child.sku}
                                                item={child}
                                                isSelected={child.sku === selectedSku}
                                                isChild
                                                hideThumbnails={isProductCopy}
                                                skuLabel={isAsin ? 'ASIN' : 'SKU ID'}
                                                onClick={() => handleSkuClick(child.sku)}
                                            />
                                                    ))}
                                                </div>
                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                        </div>
                <div className={sidebarStyles.sidebarButtons}>
                    <button
                        type="button"
                        className={sidebarStyles.btnPushToAmazon}
                        onClick={handleOpenPushModal}
                        disabled={AMAZON_MAINTENANCE_MODE || !authCtx.user?.amazon_status || allSelectableSkus.length === 0 || isNewListing}
                        title={AMAZON_MAINTENANCE_MODE ? 'Amazon integrations are temporarily under maintenance' : !authCtx.user?.amazon_status ? 'Connect your Amazon account first' : isNewListing ? 'Push to Amazon is only available for existing listings' : ''}
                    >
                        <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.00031 11.8175C7.41088 11.8175 7.7432 11.4847 7.7432 11.0746V2.52743L11.1773 5.96206C11.3224 6.10717 11.5126 6.17948 11.7028 6.17948C11.893 6.17948 12.0832 6.10717 12.2283 5.96206C12.5185 5.67184 12.5185 5.20184 12.2283 4.91161L7.52578 0.209123C7.24695 -0.0697078 6.75367 -0.0697078 6.47533 0.209123L1.77284 4.91161C1.48262 5.20184 1.48262 5.67184 1.77284 5.96206C2.06306 6.25228 2.53307 6.25228 2.82329 5.96206L6.25742 2.52743V11.0746C6.25742 11.4847 6.58974 11.8175 7.00031 11.8175Z" fill="white"/>
                            <path d="M0.743073 9.42444C0.332502 9.42444 0.000183105 9.75725 0.000183105 10.1673V13.6109C0.000183105 14.0209 0.332502 14.3538 0.743073 14.3538H13.2573C13.6679 14.3538 14.0002 14.0209 14.0002 13.6109V10.1673C14.0002 9.75725 13.6679 9.42444 13.2573 9.42444C12.8467 9.42444 12.5144 9.75725 12.5144 10.1673V12.868H1.48596V10.1673C1.48596 9.75725 1.15364 9.42444 0.743073 9.42444Z" fill="white"/>
                        </svg>
                        Push to Amazon
                    </button>
                    <button
                        type="button"
                        className={sidebarStyles.btnDownloadContent}
                        onClick={handleOpenDownloadModal}
                        disabled={allSelectableSkus.length === 0}
                    >
                        Download Content
                    </button>
                </div>

                <SkuSelectionModal
                    show={showDownloadModal}
                    onClose={() => setShowDownloadModal(false)}
                    title="Download Content"
                    buttonText="Download Content"
                    iconType="download"
                    skuList={allSelectableSkus}
                    selectedSkus={modalSelectedSkus}
                    onSkuToggle={handleModalSkuToggle}
                    onSelectAll={handleModalSelectAll}
                    onConfirm={handleDownloadConfirm}
                    isConfirming={isDownloading}
                />
                <SkuSelectionModal
                    show={showPushModal}
                    onClose={() => setShowPushModal(false)}
                    title="Push to Amazon"
                    buttonText="Push to Amazon"
                    iconType="push"
                    skuList={allSelectableSkus}
                    selectedSkus={modalSelectedSkus}
                    onSkuToggle={handleModalSkuToggle}
                    onSelectAll={handleModalSelectAll}
                    onConfirm={handlePushConfirm}
                    isConfirming={isPushingToAmazon}
                />
            </div>

            {/* Main content: SingleResultsCard */}
            <div className={sidebarStyles.mainContent}>
                {isDataLoading && !preTransformedData ? (
                    <div className={sidebarStyles.loadingState}>
                        <p>fetching product data...</p>
                    </div>
                ) : (
                    <SingleResultsCard
                    key={selectedSku}
                    resultData={resultData}
                    isInline={true}
                    hideResultsHeader={true}
                    hideProductImages={isProductCopy}
                    hideEditMoreOverlay={false}
                    hideDownloadButton={true}
                    onClose={() => {}}
                    onContentUpdated={handleContentUpdated}
                    authToken={authCtx.token}
                    />
                )}
            </div>
            </div>
        </div>
    );
};

export default BulkResultsCard;
